import { useEffect, useState } from "react";

import { useUserOptional } from "@/auth/UserContext";
import { KAI_WELCOME_PROMPTS, useFeatureEnabled } from "@/context/FeatureFlagsContext";
import { kaiApi } from "@/services/kaiApi";
import {
  getRoleWelcomePrompts,
  type KaiWelcomePrompt,
} from "@/features/kai/kaiWelcomePrompts";

export interface KaiWelcomePromptsState {
  prompts: KaiWelcomePrompt[];
  /** A personalized fetch is in flight (role prompts are shown meanwhile). */
  loading: boolean;
  /** The returned prompts are Kai-generated from real data, not the fallback. */
  personalized: boolean;
}

const cache = new Map<string, KaiWelcomePrompt[]>();
const inflightByKey = new Map<string, Promise<KaiWelcomePrompt[] | null>>();

export function peekKaiWelcomePromptCache(
  key: string,
): KaiWelcomePrompt[] | null {
  return cache.get(key) ?? null;
}

/** Test helper — clears the session cache and any in-flight request. */
export function resetKaiWelcomePromptCache(): void {
  cache.clear();
  inflightByKey.clear();
}

/**
 * Fetch Kai-generated prompts once per user session. The result is kept in a
 * module cache so the Kai page and the side rail share it across remounts
 * (navigating away and back, opening the rail on another page). In-flight
 * work is also shared, so a Strict Mode remount or an unmount mid-request
 * does not discard a successful generation.
 */
export function loadPersonalizedWelcomePrompts(
  key: string,
): Promise<KaiWelcomePrompt[] | null> {
  const hit = peekKaiWelcomePromptCache(key);
  if (hit) return Promise.resolve(hit);
  const pending = inflightByKey.get(key);
  if (pending) return pending;

  const request = kaiApi
    .getWelcomeSuggestions()
    .then((prompts) => {
      if (prompts.length > 0) {
        cache.set(key, prompts);
        return prompts;
      }
      return null;
    })
    .catch((err) => {
      console.warn("Failed to fetch Kai welcome prompts:", err);
      return null;
    })
    .finally(() => {
      inflightByKey.delete(key);
    });
  inflightByKey.set(key, request);
  return request;
}

/**
 * Warm the session cache as soon as the caller is known, so generated
 * prompts are ready when the Kai page remounts or the side rail opens.
 */
export function usePrefetchKaiWelcomePrompts(): void {
  const userCtx = useUserOptional();
  const flagEnabled = useFeatureEnabled(KAI_WELCOME_PROMPTS);
  const cacheKey = userCtx?.profile?.id ?? null;

  useEffect(() => {
    if (!flagEnabled || !cacheKey) return;
    void loadPersonalizedWelcomePrompts(cacheKey);
  }, [flagEnabled, cacheKey]);
}

/**
 * Resolve the welcome-screen starter prompts.
 *
 * Role-based curated prompts render instantly and act as the fallback. When
 * the `kai_welcome_prompts` flag is on, Kai-generated prompts are fetched
 * once per session and reused across the Kai page and the chat side rail.
 */
export function useKaiWelcomePrompts(
  isWelcome: boolean,
): KaiWelcomePromptsState {
  const userCtx = useUserOptional();
  const flagEnabled = useFeatureEnabled(KAI_WELCOME_PROMPTS);
  const cacheKey = userCtx?.profile?.id ?? null;

  const rolePrompts = getRoleWelcomePrompts({
    role: userCtx?.profile?.role ?? null,
    canViewSavings: userCtx?.canViewSavings ?? false,
    isAdmin: userCtx?.isAdmin ?? false,
    isSalesPerson: userCtx?.isSalesPerson ?? false,
    isClient: userCtx?.isClient ?? false,
  });

  const [personalized, setPersonalized] = useState<KaiWelcomePrompt[] | null>(
    () => (cacheKey ? peekKaiWelcomePromptCache(cacheKey) : null),
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!flagEnabled || !cacheKey) {
      setPersonalized(null);
      setLoading(false);
      return;
    }

    const hit = peekKaiWelcomePromptCache(cacheKey);
    if (hit) {
      setPersonalized(hit);
      setLoading(false);
      return;
    }

    if (!isWelcome) return;

    let cancelled = false;
    setLoading(true);
    loadPersonalizedWelcomePrompts(cacheKey).then((prompts) => {
      if (cancelled) return;
      if (prompts) setPersonalized(prompts);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [isWelcome, flagEnabled, cacheKey]);

  const usePersonalized = flagEnabled && personalized !== null;

  return {
    prompts: usePersonalized ? personalized! : rolePrompts,
    loading,
    personalized: usePersonalized,
  };
}
